import type { Geofence, ResolvedPlace } from '@gpx-spa/geo-domain'
import { geofenceToResolvedPlace } from '@gpx-spa/geo-domain'
import type { V2ShipmentStop } from '@gpx-spa/queries/src/getAccountShipmentV2'
import { COMPOSER_STOP_RADIUS_DEFAULT_M } from '@/components/shipment/form/domain/constants'

/** Matches create-wizard stop row shape; defined here to avoid store↔utils import cycles. */
export type MappedShipmentFormStopRow = {
  key: string
  radiusM: number
  resolved: ResolvedPlace | null
  geofence: Geofence | null
  plannedArrival: Date | null
  plannedDeparture: Date | null
  /** device-api / v2 stop id when resuming or after first persist */
  persistedStopId?: number
}

let keySeq = 0

function generateStopKey(): string {
  return `stop-${++keySeq}-${Date.now().toString(36)}`
}

function parsePlannedAt(iso: string | null | undefined): Date | null {
  if (iso == null || iso === '') return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d
}

function radiusMFromStop(stop: V2ShipmentStop): number {
  const r = stop.radius
  if (r == null) return COMPOSER_STOP_RADIUS_DEFAULT_M
  const n = Number(r)
  return Number.isFinite(n) && n > 0 ? n : COMPOSER_STOP_RADIUS_DEFAULT_M
}

function isGeofenceStop(stop: V2ShipmentStop): boolean {
  const t = String(stop.type ?? '').toLowerCase()
  if (t === 'geofence') return true
  return stop.geofence_id != null && stop.geofence_id > 0
}

function syntheticGeofenceFromStop(stop: V2ShipmentStop): Geofence {
  const id = stop.geofence_id ?? 0
  const name =
    (typeof stop.name === 'string' && stop.name.trim()) || 'Geofence'
  const g = stop.geojson?.geometry
  const base: Geofence = {
    id,
    name,
    lat: stop.center.lat,
    lng: stop.center.lng,
  }
  if (
    g &&
    typeof g === 'object' &&
    typeof (g as { type?: unknown }).type === 'string' &&
    'coordinates' in g
  ) {
    base.geo_json = {
      type: (g as { type: string }).type,
      coordinates: (g as { coordinates: unknown }).coordinates,
    } as Geofence['geo_json']
  }
  return base
}

function v2StopToRow(
  stop: V2ShipmentStop,
  geofencesById: Map<number, Geofence>,
): MappedShipmentFormStopRow {
  const key = generateStopKey()
  const radiusM = radiusMFromStop(stop)
  const plannedArrival = parsePlannedAt(stop.planned_arrival_at)
  const plannedDeparture = parsePlannedAt(stop.planned_departure_at)

  const persistedStopId =
    typeof stop.id === 'number' && stop.id > 0 ? stop.id : undefined

  if (isGeofenceStop(stop)) {
    const gid = stop.geofence_id
    const gf =
      gid != null && gid > 0
        ? geofencesById.get(gid) ?? syntheticGeofenceFromStop(stop)
        : syntheticGeofenceFromStop(stop)
    const resolved = geofenceToResolvedPlace(gf)
    return {
      key,
      radiusM,
      resolved: resolved ?? null,
      geofence: gf,
      plannedArrival,
      plannedDeparture,
      persistedStopId,
    }
  }

  const lat = stop.center.lat
  const lng = stop.center.lng
  const label =
    (typeof stop.name === 'string' && stop.name.trim()) ||
    `${lat}, ${lng}`
  const resolved: ResolvedPlace = {
    coordinates: { lat, lng },
    formattedAddress: label,
  }
  return {
    key,
    radiusM,
    resolved,
    geofence: null,
    plannedArrival,
    plannedDeparture,
    persistedStopId,
  }
}

function emptyStopRow(): MappedShipmentFormStopRow {
  return {
    key: generateStopKey(),
    radiusM: COMPOSER_STOP_RADIUS_DEFAULT_M,
    resolved: null,
    geofence: null,
    plannedArrival: null,
    plannedDeparture: null,
  }
}

/**
 * Maps device-api v2 shipment stops (list/detail) into create-wizard stop rows.
 */
export function v2ShipmentStopsToFormStopRows(
  apiStops: V2ShipmentStop[],
  geofences: readonly Geofence[],
): MappedShipmentFormStopRow[] {
  const byId = new Map<number, Geofence>()
  for (const g of geofences) {
    if (typeof g?.id === 'number') byId.set(g.id, g)
  }
  const sorted = [...apiStops].sort((a, b) => a.sequence - b.sequence)
  const rows = sorted.map((s) => v2StopToRow(s, byId))
  while (rows.length < 2) {
    rows.push(emptyStopRow())
  }
  return rows
}
